import mapboxgl from 'mapbox-gl'
import {ActionTree, GetterTree, MutationTree} from "vuex";
import {RootState} from "../store";
import {GetParkBySlug} from "../../../../utils/graphql/park-graph";
import graph_client from "../../../../utils/graphql/graph_client";
import {bbox as turfBbox} from "@turf/turf";
import {parse} from 'wkt'
import {GridRefUtils} from "../../../../utils/helpers/GridRefPreference";
import {MeasurementPrefUtility} from "../../../../utils/helpers/MeasurementPref";
import {IndexedDBCache} from "../../../../utils/crosshair_altitude_service/indexed_db_cache";
import {getElevation} from "../../../../utils/crosshair_altitude_service/pixel_methods";
import {throttle} from 'lodash-es';
import {MapLayerCategory} from "../../../../utils/layer-registry";

export class ParkMapState {
  parkId: number | null = null
  center: mapboxgl.LngLatLike = [0,0]
  zoom: number = 13
  trail: any = null
  loading: boolean = false
  mapInitialised: boolean = false
  miniCoordsBar: string = ''
  mapBoundsSet: boolean = false
  measurementMode: boolean = false
  regionSlug: string | null = null
  parkSlug: string | null = null
  countrySlug: string | null = null
  measuredGeoJson: any = null
  trailSetOnMap: boolean = false
}

const actions: ActionTree<ParkMapState, RootState> = {
  async initialiseStore(store) {
    store.state.center = window.center
  },
  async mapInitialised(store) {
    const gridRef = GridRefUtils.preference()

    window.map.addControl(
      new mapboxgl.NavigationControl({ showCompass: true, showZoom: true, visualizePitch: true }),
      'bottom-left'
    );
    let measurementPreference = MeasurementPrefUtility.preference()

    window.map.addControl(new mapboxgl.ScaleControl({ unit: measurementPreference.mapbox(), maxWidth: 150 }), 'bottom-left');

    const cache = new IndexedDBCache();

    const handleMouseMove = async (e) => {
      const coords = gridRef.convert(e.lngLat);
      const currentZoom = window.map.getZoom();
      const elevation = await getElevation(e.lngLat, currentZoom, cache);
      store.commit('setMiniCoordsBar', `${coords.toString()} ${elevation}`);
    };

    const throttledMouseMove = throttle(handleMouseMove, 20); // Adjust the throttle interval as needed

    window.map.on('mousemove', throttledMouseMove);
  },
  async getPark(store) {
    const response = await graph_client.request(GetParkBySlug, { slug: window.parkSlug });
    await store.dispatch('setBoundsOnMap', response.parkBySlug.bounds);
  },
  async setBoundsOnMap(store, bounds: string) {
    const geojson = parse(bounds)
    window.layerRegistry.addSource('park', {
      'type': 'geojson',
      'data': geojson
    })
    window.layerRegistry.addLayer({
      'id': 'park',
      'type': 'fill',
      'source': 'park', // reference the data source
      'layout': {},
      'paint': {
        'fill-color': '#f57700', // blue color fill
        'fill-opacity': 0.2
      }
    }, MapLayerCategory.Annotations, { z: 0});

    // Add a black outline around the polygon.
    window.layerRegistry.addLayer({
      'id': 'park-outline',
      'type': 'line',
      'source': 'park',
      'layout': {},
      'paint': {
        'line-color': '#f57700',
        'line-width': 1
      }
    }, MapLayerCategory.Annotations, { z: 1})
    store.dispatch('setMapBoundsFromPolygonGeojson', { geoJson: geojson, duration: 0 });
  },
  setMapBoundsFromPolygonGeojson(store, data: { geoJson: any, duration: number }) {
    const map = window.map
    const newBbox = turfBbox(data.geoJson);
    // @ts-ignore
    const bounds = mapboxgl.LngLatBounds.convert(newBbox);
    map.fitBounds(bounds, { padding: 100 });
  },
}

const mutations: MutationTree<ParkMapState> = {
  setMiniCoordsBar(state, miniCoordsBar: string) {
    state.miniCoordsBar = miniCoordsBar
  },
}

const getters: GetterTree<ParkMapState, RootState> = {
  initialMapSettings: (state) => {
    return {
      container: 'map',
      center: state.center,
      zoom: state.zoom,
      maxPitch: 85
    }
  }
}

export default class ParkMapModule {
  state: ParkMapState = new ParkMapState()
  namespaced = true
  actions = actions
  mutations = mutations
  getters = getters
}
